import { reduce } from "et3-type-guards";

import { EFieldTone } from "../../../../../constants/enum/field-tone";
import { EFieldToneRule } from "../../../../../constants/enum/field-tone-rule";
import { EFieldValueFormat } from "../../../../../constants/enum/field-value-format";
import i18n from "../../../../../constants/i18n";
import { withLiteObserverAndTheme } from "../../../../../core/et3-enhanced-render";
import Et3FieldGrid, {
  isBlankValue,
  TEt3Field
} from "../../../../../core/et3-field-grid";
import getUserAdvancedStore from "../../../../../stores/user-store/advanced-store";
import { camelCase, startCase } from "lodash";

import { getActivationLabelKey } from "../activation-labels";
import {
  AFFIRMATIVE_VALUE,
  FIELD_GROUP_COLUMNS,
  TUserFieldDefinition,
  VERIFIED_STATUS_VALUE,
  ZERO_VALUE
} from "./field-definitions";

const resolveTone = (toneRule: EFieldToneRule, value: string) => {
  switch (toneRule) {
    case EFieldToneRule.CRITICAL_WHEN_ZERO:
      return value === ZERO_VALUE ? EFieldTone.CRITICAL : EFieldTone.NEUTRAL;
    case EFieldToneRule.POSITIVE_WHEN_ACTIVATED:
      return value === VERIFIED_STATUS_VALUE
        ? EFieldTone.POSITIVE
        : EFieldTone.WARNING;
    case EFieldToneRule.POSITIVE_WHEN_YES:
      return value === AFFIRMATIVE_VALUE
        ? EFieldTone.POSITIVE
        : EFieldTone.NEUTRAL;
    default:
      return EFieldTone.NEUTRAL;
  }
};

const formatValue = (valueFormat: EFieldValueFormat, value: string) => {
  switch (valueFormat) {
    case EFieldValueFormat.TITLE_CASE:
      return startCase(camelCase(value));
    case EFieldValueFormat.ACTIVATION_LINK:
      return i18n.get(
        value === AFFIRMATIVE_VALUE ? "ACTIVATED" : "NOT_ACTIVATED"
      );
    case EFieldValueFormat.ACTIVATION_STATUS:
      return i18n.get(getActivationLabelKey(value));
    case EFieldValueFormat.TOGGLE_STATE:
      return i18n.get(value === AFFIRMATIVE_VALUE ? "ON_VALUE" : "OFF_VALUE");
    default:
      return value;
  }
};

const UserFieldGroup = withLiteObserverAndTheme<{
  definitions: TUserFieldDefinition[];
}>((props) => {
  const { definitions } = props;
  const advancedStore = getUserAdvancedStore();
  const userId = advancedStore.currentUserId.get();
  const data = advancedStore.userData.get(userId);

  const fields: TEt3Field[] = definitions.map((definition) => {
    const userKey = reduce(() => definition.userKey, "userId");
    const labelKey = reduce(() => definition.labelKey, "USER_ID");
    const isMachineValue = reduce(() => definition.isMachineValue, false);
    const toneRule = reduce(() => definition.toneRule, EFieldToneRule.NEUTRAL);
    const rawValue = reduce(() => `${data[userKey]}`, "");
    const valueFormat = reduce(
      () => definition.valueFormat,
      EFieldValueFormat.RAW
    );
    const value = isBlankValue(rawValue)
      ? rawValue
      : formatValue(valueFormat, rawValue);
    return {
      labelKey,
      value,
      tone: resolveTone(toneRule, rawValue),
      isMachineValue
    };
  });

  return (
    <Et3FieldGrid fields={fields} columns={FIELD_GROUP_COLUMNS} width={0} />
  );
});
export default UserFieldGroup;
